import type {
  ForgeProvider,
  RunForgeCredentialView,
  RunForgeCredentialViewReason,
} from '@/lib/api/generated/model';

import { PERSONAL_ACCOUNT_COPY } from './copy';
import { forgeName } from './forgeAccounts';

/**
 * Workspaces that require each person's own forge account (spec 160): why a
 * start was refused or a run blocked, in words. Pure: no React, no client.
 */

export const LINKED_ACCOUNTS_PATH = '/settings/commits' as const;
export const LINKED_ACCOUNTS_HASH = 'linked-accounts';

export type MissingReason = NonNullable<RunForgeCredentialViewReason>;

/** A forge host the person has no usable link for, and why. */
export interface MissingHost {
  provider: ForgeProvider;
  host: string;
  reason: MissingReason;
}

const MISSING_WORDS: Record<MissingReason, string> = {
  not_linked: 'not linked',
  broken: 'the link needs relinking',
  expired: 'the token expired',
  revoked: 'removed by an administrator',
  inactive_owner: 'no longer active',
  unreachable: 'the forge could not be reached',
};

export function missingWords(reason: MissingReason): string {
  return MISSING_WORDS[reason];
}

/** Every host had a link once: the person relinks rather than links. */
export function relinkNeeded(hosts: readonly MissingHost[]): boolean {
  return (
    hosts.length > 0 && hosts.every((host) => host.reason !== 'not_linked')
  );
}

const CATEGORY_PREFIX = 'personal_account_';

function isMissingReason(value: string): value is MissingReason {
  return value in MISSING_WORDS;
}

/**
 * A refused start, from its 422 body (spec 140): one violation per host,
 * `category` `personal_account_<reason>` and `path` the host. Read
 * defensively, as spec 159's token refusal is; anything else is not ours.
 *
 * The violation does not name the forge, so the service's own sentence does:
 * it says "GitHub" for a GitHub host.
 */
export function personalAccountRefusal(
  body: unknown,
): { message: string; hosts: MissingHost[] } | null {
  if (typeof body !== 'object' || body === null || !('violations' in body)) {
    return null;
  }
  const { violations } = body;
  if (!Array.isArray(violations)) return null;
  const hosts: MissingHost[] = [];
  const messages: string[] = [];
  for (const violation of violations as unknown[]) {
    if (typeof violation !== 'object' || violation === null) continue;
    const { path, category, message } = violation as {
      path?: unknown;
      category?: unknown;
      message?: unknown;
    };
    if (
      typeof category !== 'string' ||
      !category.startsWith(CATEGORY_PREFIX) ||
      typeof path !== 'string'
    ) {
      continue;
    }
    const reason = category.slice(CATEGORY_PREFIX.length);
    if (!isMissingReason(reason)) continue;
    const text = typeof message === 'string' ? message : '';
    hosts.push({
      provider: /\bGitHub\b/.test(text) ? 'github' : 'gitlab',
      host: path,
      reason,
    });
    if (text && !messages.includes(text)) messages.push(text);
  }
  if (hosts.length === 0) return null;
  return { message: messages.join(' '), hosts };
}

/** The hosts a blocked run lacked, from what it recorded per forge. */
export function hostsFromCredentials(
  credentials: readonly RunForgeCredentialView[] | undefined,
): MissingHost[] {
  return (credentials ?? [])
    .filter((credential) => credential.source === 'required')
    .map((credential) => ({
      provider: credential.provider,
      host: credential.host,
      reason: credential.reason ?? 'not_linked',
    }));
}

/**
 * Why a run was blocked, for whoever reads it: "Needs your own GitLab account
 * on h (not linked)." for its person, "Needs Jane Doe's own …" for anyone
 * else. Composed here rather than shown from the run's record, which always
 * says "your".
 */
export function blockedAccountSentence(
  hosts: readonly MissingHost[],
  person: string | null,
): string {
  const whose = person === null ? 'your' : `${person}'s`;
  const parts = hosts.map(
    (host) =>
      `${forgeName(host.provider)} account on ${host.host} (${missingWords(host.reason)})`,
  );
  return `Needs ${whose} own ${parts.join('; ')}.`;
}

/** "Link account", or "Relink account" when every host had a link once. */
export function linkActionLabel(hosts: readonly MissingHost[]): string {
  return relinkNeeded(hosts)
    ? PERSONAL_ACCOUNT_COPY.relink
    : PERSONAL_ACCOUNT_COPY.link;
}
